import { apiClient } from './lib/api-client';
import './style.css';

interface PracticeJob {
  id: string;
  title: string;
  company: string;
  location: string;
  industry: string;
  experience_level: string;
  difficulty_rating: number;
  estimated_duration: number;
  short_description: string;
  cover_image_url: string | null;
  tags: string[];
}

interface PracticeJobsResponse {
  success: boolean;
  data: PracticeJob[];
  metadata: {
    total_count: number;
    query_type: string;
    timestamp: string;
    function: string;
  };
}

// Initialize the app
document.querySelector<HTMLDivElement>('#app')!.innerHTML = `
  <div class="container">
    <header class="hero">
      <h1>Practice AI Interviews</h1>
      <p class="subtitle">Get better at getting hired</p>
      <p class="description">100+ realistic AI interviews across all industries. Free forever.</p>
    </header>
    
    <main class="content">
      <div class="filters-section">
        <div class="search-bar">
          <input type="text" id="search-input" placeholder="Search by job title, skill, or keyword..." />
          <button id="search-btn" type="button" class="btn btn-primary">Search</button>
        </div>
        
        <div class="filter-controls">
          <select id="industry-filter">
            <option value="">All Industries</option>
            <option value="logistics">Logistics</option>
            <option value="technology">Technology</option>
            <option value="healthcare">Healthcare</option>
            <option value="construction">Construction</option>
            <option value="retail">Retail</option>
            <option value="finance">Finance</option>
          </select>
          
          <select id="level-filter">
            <option value="">All Experience Levels</option>
            <option value="entry">Entry Level</option>
            <option value="mid">Mid Level</option>
            <option value="senior">Senior Level</option>
            <option value="executive">Executive</option>
          </select>
          
          <button id="clear-filters" type="button" class="btn btn-secondary">Clear Filters</button>
        </div>
      </div>
      
      <div id="loading" class="loading" style="display: none;">
        <p>Loading practice interviews...</p>
      </div>
      
      <div id="error" class="error" style="display: none;">
        <p>Failed to load practice interviews. Please try again.</p>
        <button id="retry-btn" type="button" class="btn btn-primary">Retry</button>
      </div>
      
      <div id="results-section" class="results-section">
        <div class="results-header">
          <h2 id="results-title">Available Practice Interviews</h2>
          <span id="results-count" class="results-count"></span>
        </div>
        
        <div id="jobs-grid" class="jobs-grid">
          <!-- Jobs will be dynamically inserted here -->
        </div>
      </div>
    </main>
    
    <footer>
      <p>&copy; 2025 KeySingularity - Practice AI Interviews</p>
    </footer>
  </div>
`;

// State management
let allJobs: PracticeJob[] = [];
let filteredJobs: PracticeJob[] = [];

// DOM elements
const searchInput = document.querySelector<HTMLInputElement>('#search-input')!;
const searchBtn = document.querySelector<HTMLButtonElement>('#search-btn')!;
const industryFilter = document.querySelector<HTMLSelectElement>('#industry-filter')!;
const levelFilter = document.querySelector<HTMLSelectElement>('#level-filter')!;
const clearFiltersBtn = document.querySelector<HTMLButtonElement>('#clear-filters')!;
const loadingDiv = document.querySelector<HTMLDivElement>('#loading')!;
const errorDiv = document.querySelector<HTMLDivElement>('#error')!;
const retryBtn = document.querySelector<HTMLButtonElement>('#retry-btn')!;
// const resultsTitle = document.querySelector<HTMLHeadingElement>('#results-title')!;
const resultsCount = document.querySelector<HTMLSpanElement>('#results-count')!;
const jobsGrid = document.querySelector<HTMLDivElement>('#jobs-grid')!;

// Load practice jobs
async function loadPracticeJobs() {
  try {
    showLoading();
    const response: PracticeJobsResponse = await apiClient.getPracticeJobs({
      query: 'browse-all',
      limit: 100
    });
    
    if (response.success) {
      allJobs = response.data;
      filteredJobs = [...allJobs];
      renderJobs();
      hideLoading();
    } else {
      showError();
    }
  } catch (error) {
    console.error('Failed to load practice jobs:', error);
    showError();
  }
}

// Render job cards
function renderJobs() {
  if (filteredJobs.length === 0) {
    jobsGrid.innerHTML = `
      <div class="no-results">
        <h3>No interviews found</h3>
        <p>Try adjusting your search or filters</p>
      </div>
    `;
    resultsCount.textContent = '0 interviews';
    return;
  }

  resultsCount.textContent = `${filteredJobs.length} interview${filteredJobs.length === 1 ? '' : 's'}`;
  
  jobsGrid.innerHTML = filteredJobs.map(job => `
    <div class="job-card" onclick="startInterview('${job.id}')">
      <div class="job-card-header">
        <div class="job-meta">
          <span class="industry-tag">${job.industry}</span>
          <span class="duration-tag">${job.estimated_duration} min</span>
        </div>
      </div>
      
      <div class="job-card-content">
        <h3 class="job-title">${job.title}</h3>
        <p class="job-company">${job.company}</p>
        <p class="job-location">${job.location}</p>
        <p class="job-description">${job.short_description}</p>
      </div>
      
      <div class="job-card-footer">
        <div class="difficulty-rating">
          ${generateStarRating(job.difficulty_rating)}
        </div>
        <span class="experience-level">${job.experience_level}</span>
      </div>
      
      <div class="job-tags">
        ${job.tags.slice(0, 3).map(tag => `<span class="tag">${tag.replace(/_/g, ' ')}</span>`).join('')}
      </div>
      
      <button type="button" class="start-interview-btn">Start Interview</button>
    </div>
  `).join('');
}

// Generate star rating HTML
function generateStarRating(rating: number): string {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(i <= rating ? '★' : '☆');
  }
  return stars.join('');
}

// Filter functions
function applyFilters() {
  const searchTerm = searchInput.value.toLowerCase().trim();
  const selectedIndustry = industryFilter.value;
  const selectedLevel = levelFilter.value;

  filteredJobs = allJobs.filter(job => {
    const matchesSearch = !searchTerm || 
      job.title.toLowerCase().includes(searchTerm) ||
      job.company.toLowerCase().includes(searchTerm) ||
      job.short_description.toLowerCase().includes(searchTerm) ||
      job.tags.some(tag => tag.toLowerCase().includes(searchTerm));
    
    const matchesIndustry = !selectedIndustry || job.industry === selectedIndustry;
    const matchesLevel = !selectedLevel || job.experience_level === selectedLevel;
    
    return matchesSearch && matchesIndustry && matchesLevel;
  });
  
  renderJobs();
}

// UI state functions
function showLoading() {
  loadingDiv.style.display = 'block';
  errorDiv.style.display = 'none';
  jobsGrid.style.display = 'none';
}

function hideLoading() {
  loadingDiv.style.display = 'none';
  jobsGrid.style.display = 'grid';
}

function showError() {
  loadingDiv.style.display = 'none';
  errorDiv.style.display = 'block';
  jobsGrid.style.display = 'none';
}

// Start interview function (placeholder)
function startInterview(jobId: string) {
  const job = allJobs.find(j => j.id === jobId);
  if (job) {
    // TODO: Navigate to interview interface
    alert(`Starting interview for: ${job.title}\nThis will redirect to the interview interface.`);
  }
}

// Event listeners
searchBtn.addEventListener('click', applyFilters);
searchInput.addEventListener('keypress', (e) => {
  if (e.key === 'Enter') {
    applyFilters();
  }
});
industryFilter.addEventListener('change', applyFilters);
levelFilter.addEventListener('change', applyFilters);

clearFiltersBtn.addEventListener('click', () => {
  searchInput.value = '';
  industryFilter.value = '';
  levelFilter.value = '';
  filteredJobs = [...allJobs];
  renderJobs();
});

retryBtn.addEventListener('click', loadPracticeJobs);

// Make startInterview available globally
(window as any).startInterview = startInterview;

// Initialize app
loadPracticeJobs();